import React, { useEffect, useMemo, useRef, useState } from "react";
import { INVENTORY_STATUS, TUnitTypeListingMdl } from "units/_models/UnitTypeMdl";
import { UnitTypeStore } from "unitTypes/_store/UnitTypeStore";
import { UnitTypeContext } from "unitTypes/_store/UnitTypeContext";
import { GALLERY_NAME_MODAL, GALLERY_TABS } from "_configs/propertyConfig";
import { GalleryListModal } from "units/UnitViewGalleryListModal";
import { useTranslation } from "react-i18next";
import { UnitGallery } from "units/UnitGallery";
import ShareButton from "properties/propertyPage/propertyView/ShareButton";
import { unitsStore } from "units/_stores/unitsStore";
import { TFilterType } from "admin/_common/resources/ResourceFilterMdl";
import { UnitTypeHeader } from "unitTypes/UnitTypeHeader";
import { LoadableFromLoading } from "_common/loaders/LoadableFromLoading";
import { usePropertyStore } from "properties/propertyPage/PropertyContext";
import { UnitTypeInventory } from "unitTypes/UnitTypeInventory";
import { PhaseEarlyUnitType } from "unitTypes/PhaseEarlyUnitType";
import { INVENTORY_FILTERS_KEYS } from "_configs/filtersConfig";
import { UnitCheckList } from "units/UnitCheckList";
import { UnitFloorPlans } from "units/UnitFloorPlans";
import { UNIT_OPTION } from "_configs/unitConfig";
import UnitDescription from "units/UnitDescription";
import UnitScrollButtons from "units/UnitScrollButton";
import { galleryStore } from "gallery/_store/GalleryStore";
import { COMMON_RESOURCE } from "_common/resources/BaseResourceStore";
import scrollImg from "../assets/images/icons/scroll.svg";

type Props = {
    unitType: TUnitTypeListingMdl;
};

export function UnitTypePage({ unitType }: Props) {
    const unitTypeStore = useMemo(() => new UnitTypeStore(unitType), [unitType]);
    const { t } = useTranslation();
    const { property } = usePropertyStore();
    const floorPlanRef = useRef<HTMLDivElement | null>(null);
    const checklistRef = useRef<HTMLDivElement | null>(null);
    const inventoryRef = useRef<HTMLDivElement | null>(null);
    const elementRef = useRef<HTMLDivElement | null>(null);
    const [isAtBottom, setIsAtBottom] = useState(false);

    const scrollToFloorPlan = () => {
        floorPlanRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const scrollToChecklist = () => {
        checklistRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const scrollToInventory = () => {
        inventoryRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        const handleScroll = () => {
            const element = elementRef.current;
            if (element) {
                const { scrollTop, scrollHeight, clientHeight } = element;
                const isScrolledToBottom = Math.abs(scrollTop + clientHeight - scrollHeight) < 1;
                setIsAtBottom(isScrolledToBottom);
            }
        };

        const element = elementRef.current;
        if (element) {
            element.addEventListener("scroll", handleScroll);
            return () => {
                element.removeEventListener("scroll", handleScroll);
            };
        }
    }, []);

    const listStore = unitsStore.getListStore("unit-type-" + unitType._id, Number.MAX_SAFE_INTEGER, [
        { id: "propertyId", type: TFilterType.ID, value: property._id },
        { id: "unitTypeIds", type: TFilterType.ID, value: unitType._id },
        { id: "duplicateHidden", type: TFilterType.BOOLEAN, value: true },
        {
            id: INVENTORY_FILTERS_KEYS.STATUS,
            value: INVENTORY_STATUS.AVAILABLE,
            type: TFilterType.ENUM,
        },
    ]);

    useEffect(() => {
        galleryStore.setModalName(GALLERY_NAME_MODAL.UNIT_TYPE);

        return () => {
            galleryStore.setModalName(GALLERY_NAME_MODAL.PROPERTY);
        };
    }, []);

    return (
        <UnitTypeContext unitTypeStore={unitTypeStore}>
            <div className="my-5 mx-5 md:mx-16 relative h-[95vh] overflow-y-auto" ref={elementRef}>
                <div className="hidden sm:flex justify-between mb-5">
                    <div className="flex items-baseline gap-2">
                        <LoadableFromLoading
                            loading={listStore.currentLoadingState}
                            renderer={() => {
                                return <PhaseEarlyUnitType />;
                            }}
                        />
                    </div>
                    <div className="flex mr-8 sm:mr-0 gap-x-2">
                        <ShareButton />
                    </div>
                </div>
                <UnitGallery images={unitType.photos ?? []} />
                <LoadableFromLoading
                    loading={listStore.currentLoadingState}
                    renderer={() => {
                        return <UnitTypeHeader />;
                    }}
                />
                <UnitScrollButtons
                    unit={unitType}
                    title={t("unitPage.form.title", {
                        unit: unitType.localized.title,
                        property: property.localized.title,
                    })}
                    scrollToFloorPlan={scrollToFloorPlan}
                    scrollToChecklist={scrollToChecklist}
                    scrollToInventory={scrollToInventory}
                />
                <LoadableFromLoading
                    loading={listStore.currentLoadingState}
                    renderer={() => {
                        return (
                            <div ref={inventoryRef}>
                                <UnitTypeInventory />
                            </div>
                        );
                    }}
                />

                {unitType.features && (
                    <div ref={checklistRef}>
                        <UnitCheckList
                            title={t("unit.featuresTitle")}
                            datas={Object.entries(unitType.features).filter(
                                ([key]) => unitType.features?.[key as keyof typeof UNIT_OPTION],
                            )}
                        />
                    </div>
                )}
                {unitType.localized.description && <UnitDescription description={unitType.localized.description} />}

                <div ref={floorPlanRef}>
                    <UnitFloorPlans floorPlans={unitTypeStore.unitType.floorPlans} />
                </div>
                {!isAtBottom && (
                    <div className="fixed bottom-20 bg-gray-200/50 rounded-full animate-bounce left-1/2 lg:left-[45%] transform -translate-x-1/2 p-3 z-30 shadow-md select-none">
                        <img src={scrollImg} className="h-7 w-7" alt="scrollImage" />
                    </div>
                )}
            </div>
            <GalleryListModal
                item={unitType}
                galleryImages={[
                    {
                        images: unitType.photos ?? [],
                        tabName: GALLERY_TABS.GALLERY,
                        label: t("property.galleryNavbar.mainGallery"),
                    },
                    {
                        images: unitType.floorPlans ?? [],
                        tabName: GALLERY_TABS.LEVEL_PLAN,
                        label: t("property.galleryNavbar.levelPlan"),
                    },
                    {
                        images: property.photos,
                        tabName: GALLERY_TABS.PROPERTY_PHOTOS,
                        label: t("property.galleryNavbar.propertyPhotos"),
                    },
                ]}
                resource={COMMON_RESOURCE.UNIT_TYPES}
            />
        </UnitTypeContext>
    );
}
